// 클럽 설정 페이지 — 운영진 전용. 클럽 이름/단축명/지역/소개/연락처/로고/NTRP
// 공개범위를 한 폼에 묶어서 PUT 한 방에 저장.
//
// 클럽 이름은 read-only(페어매치 팀이 직접 처리). 로고는 별도 엔드포인트로
// 업로드(서버에서 S3 putObject) 후 반환된 URL을 폼 payload에 담는다.
//
// 저장 성공 시 state.club을 갱신하고 home으로 이동.

import { useRef, useState } from "preact/hooks";
import { api } from "../api";
import { resizeImageToDataUrl } from "../matches";
import { navigate } from "../routes";
import { persist, state } from "../state";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";
import { toast, withBusy } from "../ui/sheet";

const NTRP_VIS_OPTIONS = [
  { value: "staff_only", label: "운영진만 (비공개)" },
  { value: "all_members", label: "전체 회원 공개" },
];

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

function ClubSettingsForm({ club }: { club: any }) {
  const [shortName, setShortName] = useState<string>(club.short_name || "");
  const [region, setRegion] = useState<string>(club.region || "");
  const [description, setDescription] = useState<string>(club.description || "");
  const [supportContact, setSupportContact] = useState<string>(club.support_contact || "");
  const [logoUrl, setLogoUrl] = useState<string | null>(club.logo_url || null);
  const [ntrpVis, setNtrpVis] = useState<string>(club.ntrp_visibility || "staff_only");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pickBtnRef = useRef<HTMLButtonElement>(null);

  async function onLogoFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast("PNG/JPEG/WEBP만 지원해요");
      return;
    }
    try {
      await withBusy(pickBtnRef.current, "업로드 중…", async () => {
        const resized = await resizeImageToDataUrl(file, 512);
        const base64 = resized.dataUrl.split(",")[1];
        const res = await api("/api/v1/admin/club-settings/logo", {
          method: "POST",
          body: { content_type: resized.type, data: base64 },
        });
        setLogoUrl(res.logo_url);
      });
    } catch (err: any) {
      toast(err.message || "업로드에 실패했어요");
    }
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    const payload = {
      short_name: shortName.trim() || null,
      region: region.trim() || null,
      description: description.trim() || null,
      support_contact: supportContact.trim() || null,
      logo_url: logoUrl,
      ntrp_visibility: ntrpVis,
    };
    if (!payload.short_name) {
      toast("단축명을 입력해주세요");
      return;
    }
    try {
      const res = await api("/api/v1/admin/club-settings", { method: "PUT", body: payload });
      if (res.club) state.club = res.club;
      persist();
      toast("클럽 설정이 저장됐어요");
      navigate({ name: "home" });
    } catch (err: any) {
      toast(err.message || "저장에 실패했어요");
    }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="section">
        <div class="section__title"><h3>클럽 프로필</h3></div>

        <div class="field">
          <span class="field__label">로고</span>
          <div class="logo-row">
            <div class={"logo-preview" + (logoUrl ? "" : " logo-preview--empty")}>
              {logoUrl ? (
                <img src={logoUrl} alt="club logo" />
              ) : (
                <span class="logo-preview__placeholder">로고 없음</span>
              )}
            </div>
            <div class="logo-row__actions">
              <button
                type="button"
                ref={pickBtnRef}
                class="btn btn--ghost btn--sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {logoUrl ? "로고 교체" : "로고 업로드"}
              </button>
              {logoUrl && (
                <button
                  type="button"
                  class="btn btn--ghost btn--sm"
                  onClick={() => setLogoUrl(null)}
                >
                  제거
                </button>
              )}
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/png,image/jpeg,image/webp"
            style="display:none"
            onChange={onLogoFile}
          />
          <span class="field__hint">PNG/JPEG/WEBP, 최대 512KB (자동 리사이즈)</span>
        </div>

        <div class="field">
          <span class="field__label">클럽 이름</span>
          <div class="input input--readonly">{club.name || "-"}</div>
          <span class="field__hint">변경은 페어매치 팀에 문의해주세요</span>
        </div>

        <div class="field">
          <span class="field__label">단축명</span>
          <input
            class="input"
            type="text"
            value={shortName}
            maxLength={20}
            placeholder="예: 하이볼"
            onInput={(e) => setShortName((e.currentTarget as HTMLInputElement).value)}
          />
          <span class="field__hint">홈화면 아이콘·좁은 UI에 쓰임 (예: 하이볼)</span>
        </div>

        <div class="field">
          <span class="field__label">지역</span>
          <input
            class="input"
            type="text"
            value={region}
            maxLength={40}
            placeholder="예: 서울 강남"
            onInput={(e) => setRegion((e.currentTarget as HTMLInputElement).value)}
          />
        </div>

        <div class="field">
          <span class="field__label">소개</span>
          <textarea
            class="input"
            rows={2}
            maxLength={200}
            placeholder="클럽을 한 줄로 소개해 주세요"
            value={description}
            onInput={(e) => setDescription((e.currentTarget as HTMLTextAreaElement).value)}
          />
        </div>

        <div class="field">
          <span class="field__label">문의 연락처</span>
          <input
            class="input"
            type="text"
            value={supportContact}
            maxLength={80}
            placeholder="카카오톡, 이메일, 전화 등"
            onInput={(e) => setSupportContact((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
      </div>

      <div class="section">
        <div class="section__title"><h3>운영 설정</h3></div>
        <div class="field">
          <span class="field__label">NTRP 공개 범위</span>
          <select
            class="input"
            value={ntrpVis}
            onChange={(e) => setNtrpVis((e.currentTarget as HTMLSelectElement).value)}
          >
            {NTRP_VIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span class="field__hint">
            비공개 시 운영진만 NTRP를 볼 수 있어요. 공개 시 전체 회원에게 표시돼요.
          </span>
        </div>
      </div>

      <button class="btn btn--primary btn--block" type="submit">
        <Icon name="check" size={16} />
        저장
      </button>
    </form>
  );
}

export async function renderClubSettings(view: HTMLElement): Promise<void> {
  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let club: any;
  try {
    const res = await api("/api/v1/admin/club-settings");
    club = res.club;
  } catch (e: any) {
    view.innerHTML = "";
    const err = document.createElement("div");
    err.className = "error-card";
    err.textContent = e.status === 403 ? "관리자 모드에서만 이용할 수 있어요" : e.message;
    view.append(err);
    return;
  }

  view.innerHTML = "";
  view.append(mountPreact(<ClubSettingsForm club={club} />));
}
